'use client';

import { useState } from 'react';
import Link from 'next/link';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { ChevronRight } from 'lucide-react';

// FAQ data - API ready
const faqCategories = [
    {
        id: 'buying',
        name: 'Buying Property',
        icon: '🏠',
        faqs: [
            { q: 'How do I search for properties?', a: 'Use our search bar on the homepage to find properties by city, locality, or type. You can also use filters to narrow down your search by budget, BHK, and more.' },
            { q: 'Are all properties verified?', a: 'Properties with the "Verified" badge have been checked by our team for accuracy. We recommend always visiting the property before making a decision.' },
            { q: 'How do I contact a property owner?', a: 'Click on the "Contact Owner" button on any property page to send an enquiry. You can also call directly if the owner has enabled phone visibility.' },
            { q: 'What documents do I need to buy a property?', a: 'You\'ll typically need ID proof, address proof, PAN card, and bank statements for loan processing. Our legal services team can guide you through the complete documentation.' },
        ]
    },
    {
        id: 'selling',
        name: 'Selling Property',
        icon: '💰',
        faqs: [
            { q: 'How do I list my property?', a: 'Click on "Post Property" in the navigation bar and follow the simple 5-step process. You can add photos, set your price, and provide all property details.' },
            { q: 'Is listing my property free?', a: 'Yes! Basic listing is absolutely free. You can upgrade to premium plans for better visibility and more features.' },
            { q: 'How long does it take for my property to go live?', a: 'Most listings are reviewed and published within 24 hours. Premium listings get faster approval.' },
            { q: 'How can I get more enquiries?', a: 'Add high-quality photos, write a detailed description, and consider upgrading to a premium plan for featured placement.' },
        ]
    },
    {
        id: 'renting',
        name: 'Renting',
        icon: '🔑',
        faqs: [
            { q: 'How do I find rental properties?', a: 'Use our Rent section to browse available properties. Filter by location, budget, furnishing, and other preferences.' },
            { q: 'What is the typical security deposit?', a: 'Security deposits vary by city and property. In most cities, it\'s 2-3 months rent for unfurnished and 6-10 months for furnished properties.' },
            { q: 'Can I negotiate the rent?', a: 'Yes, most landlords are open to negotiation. Use the contact form to discuss terms directly with the owner.' },
        ]
    },
    {
        id: 'account',
        name: 'Account & Support',
        icon: '⚙️',
        faqs: [
            { q: 'How do I create an account?', a: 'Click on "Register" at the top of the page and fill in your details. You can also sign up using Google or Facebook.' },
            { q: 'I forgot my password. What should I do?', a: 'Click on "Forgot Password" on the login page and enter your email. We\'ll send you a link to reset your password.' },
            { q: 'How do I contact customer support?', a: 'You can reach us via email at support@dreamacres.com or call us at +91 98765 43210. We\'re available Mon-Sat, 9 AM - 7 PM.' },
        ]
    },
];

export default function HelpPage() {
    const [activeCategory, setActiveCategory] = useState('buying');
    const [openFaq, setOpenFaq] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const currentCategory = faqCategories.find(cat => cat.id === activeCategory);

    const filteredFaqs = searchQuery
        ? faqCategories.flatMap(cat =>
            cat.faqs.filter(faq =>
                faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.a.toLowerCase().includes(searchQuery.toLowerCase())
            ).map(faq => ({ ...faq, category: cat.name }))
        )
        : currentCategory?.faqs || [];

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray-50">
                {/* Hero / Header Section */}
                <section className="relative overflow-hidden text-white py-16 lg:py-20">
                    <img
                        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&auto=format&fit=crop&q=80"
                        alt="DreamAcres Help Center"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/80 to-primary-dark/85" />
                    <div className="absolute inset-0 bg-black/25" />

                    <div className="container-custom relative z-10">
                        <nav className="flex items-center gap-2 text-xs lg:text-sm text-white/80 mb-6 font-medium">
                            <Link href="/" className="hover:text-white transition-colors">Home</Link>
                            <ChevronRight className="w-4 h-4 opacity-70" />
                            <span className="text-white font-semibold">Help & FAQ</span>
                        </nav>

                        <h1 className="text-3xl lg:text-4xl font-bold mb-4 text-center">
                            How can we help you?
                        </h1>
                        <p className="text-white/80 text-center mb-8 max-w-xl mx-auto">
                            Find answers to frequently asked questions or contact our support team.
                        </p>

                        {/* Search */}
                        <div className="max-w-xl mx-auto">
                            <div className="relative">
                                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search for answers..."
                                    className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary shadow-lg bg-white/95 backdrop-blur-sm"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-12">
                    <div className="container-custom">
                        <div className="grid lg:grid-cols-4 gap-8">
                            {/* Categories Sidebar */}
                            <div className="lg:col-span-1">
                                <div className="bg-white rounded-xl p-4 shadow-sm sticky top-4">
                                    <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
                                    <div className="space-y-1">
                                        {faqCategories.map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => {
                                                    setActiveCategory(cat.id);
                                                    setSearchQuery('');
                                                    setOpenFaq(null);
                                                }}
                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeCategory === cat.id && !searchQuery
                                                        ? 'bg-primary text-white'
                                                        : 'text-gray-600 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <span>{cat.icon}</span>
                                                <span className="font-medium">{cat.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* FAQ List */}
                            <div className="lg:col-span-3">
                                <div className="bg-white rounded-xl shadow-sm">
                                    <div className="p-6 border-b border-gray-200">
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {searchQuery ? `Search results for "${searchQuery}"` : currentCategory?.name}
                                        </h2>
                                    </div>

                                    <div className="divide-y divide-gray-200">
                                        {filteredFaqs.length === 0 ? (
                                            <div className="p-8 text-center text-gray-500">
                                                No results found. Try a different search term.
                                            </div>
                                        ) : (
                                            filteredFaqs.map((faq, idx) => (
                                                <div key={idx} className="p-6">
                                                    <button
                                                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                                        className="w-full flex items-start justify-between text-left"
                                                    >
                                                        <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                                                        <svg
                                                            className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''
                                                                }`}
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                    {openFaq === idx && (
                                                        <div className="mt-4 text-gray-600 leading-relaxed">
                                                            {faq.a}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Contact CTA */}
                                <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-xl text-center">
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                                        Still have questions?
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        Can't find what you're looking for? Our support team is here to help.
                                    </p>
                                    <Link href="/contact" className="btn btn-primary">
                                        Contact Support
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
